import React, { useEffect, useState, useCallback, useRef } from "react";
import { useAdvancedValidation } from "hooks/useAdvancedValidation";
import "./EditSubscriberDetails.css";
import MainLayout from "components/layout/main-layout/MainLayout";
import MessageSnackBar from "components/widgets/message-snackbar/MessageSnackBar";
import { titles } from "constants/titles";
import SubscriberInformationCard from "components/layout/subscriber-information-card/SubscriberInformationCard";
import CustomInput from "components/widgets/custom-input/CustomInput";
import AddAccountContactModal from "components/layout/add-account-contact-modal/AddAccountContactModal";
import ConfirmationModal from "components/layout/confirmation-modal/ConfirmationModal";
import ChangesSavedModal from "components/layout/changes-saved-modal/ChangesSavedModal";
import InformationModal from "components/layout/information-modal/InformationModal";
import {
  IconQuestion,
  IconTrash,
  IconEdit,
  DragHandleIcon,
} from "assets/SVGs/SVGs";
import CustomButton from "components/widgets/custom-button/CustomButton";
import CustomCheckbox from "components/widgets/custom-checkbox/CustomCheckbox";
import CustomSelectBox from "components/widgets/custom-select-box/CustomSelectBox";
import { useDispatch, useSelector } from "react-redux";
import {
  formatDate,
  formatPhoneNumber,
  getAllRegionListNames,
  getDataFromId,
} from "utility";
import {
  getPhoneTypeList,
  getRegionList,
  setSelectedPhoneType,
} from "redux/Common/action";
import CustomDatePicker from "components/widgets/custom-date-picker/CustomDatePicker";
import { useHistory, useLocation } from "react-router-dom";
import { FIELD_CONFIG, VALIDATION_FIELDS } from "./config";
import * as helpers from "./helpers";

// Constants for repetitive usage
const ICON_PROPS = {
  questionIcon: {
    fillColor: "var(--blackText)",
    width: "24",
    height: "24",
  },
  actionIcon: {
    color: "#595252",
  },
};

const EditSubscriberDetails = () => {
  const [deleteContactModal, setDeleteContactModal] = useState(false);
  const [subscriberState, setSubscriberState] = useState({
    street: "",
    city: "",
    state: "",
    zip_code: "",
    apt: "",
    dateOfBirth: new Date(),
    isAcceptSignature: false,
  });

  // Validation states
  const [validationTriggered, setValidationTriggered] = useState(false);
  const [errorState, setErrorState] = useState({
    show: false,
    fieldErrors: { ...VALIDATION_FIELDS },
    fieldMessages: { ...VALIDATION_FIELDS }
  });

  // Scroll handling for snackbar animation (exactly like AccountHolder)
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    // Find the layout_container which is the actual scrolling element
    const scrollContainer = document.querySelector('.layout_container');

    const handleScroll = (e) => {
      const currentScrollY = e.target.scrollTop || 0;
      setScrollY(currentScrollY);
    };

    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }
  }, []);

  const snackbarTop = helpers.calculateSnackbarTop(scrollY);

  // Use advanced validation hook for MessageSnackBar
  const {
    snackbarState,
    dismissSnackbar,
    onApiResponse,
    handleApiResponse,
  } = useAdvancedValidation(FIELD_CONFIG);

  const [showAddContactModal, setShowAddContactModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [newContact, setNewContact] = useState({
    phoneNumber: "",
    type: "",
    type_id: "",
    auto_notify: false,
  });
  const [phoneList, setPhoneList] = useState([]);
  const previousMedicalDataRef = useRef(null);
  const [changesSavedModal, setChangesSaveModal] = useState(false);
  const [infoModalPopup, setInfoModalPopup] = useState();

  const location = useLocation();
  const medicalUserDetail = location?.state?.medicalUserDetail;
  const phoneTypeList = location?.state?.phoneTypeList;

  const allRegionList = useSelector((store) => store?.common?.allRegionList);
  const { loading } = useSelector((store) => store?.medicalAlertUsers);
  const { accountHolderData } = useSelector((store) => store?.userDetail);
  const userData = useSelector((store) => store?.auth?.userData);

  const dispatch = useDispatch();
  const history = useHistory();

  const setInitialData = useCallback(() => {
    const protected_address = medicalUserDetail?.protected_address;

    // Get region name inline to avoid dependency issues
    const getRegionName = (id) => {
      const region = allRegionList?.find((ele) => {
        return parseInt(ele?.region_id) === parseInt(id);
      });
      return region ? {
        id: region?.region_id,
        value: region?.region_code,
        label: region?.region_name
      } : "";
    };

    const stateValue = getRegionName(protected_address?.region_id);

    setSubscriberState(prevState => ({
      ...prevState,
      street: protected_address?.street,
      apt: protected_address?.street2,
      city: protected_address?.city ?? "",
      state: stateValue,
      zip_code: protected_address?.zip ?? "",
      dateOfBirth: medicalUserDetail?.date_of_birth
        ? formatDate(medicalUserDetail?.date_of_birth)
        : new Date(),
    }));

    let cloneData = [];
    const phoneType1 = getDataFromId(
      phoneTypeList,
      "phone_type_id",
      protected_address?.mobile_type_id
    );
    const phoneType2 = getDataFromId(
      phoneTypeList,
      "phone_type_id",
      protected_address?.cellphone_phone_type_id
    );
    if (protected_address?.mobile) {
      cloneData?.push({
        phoneNumber: protected_address?.mobile,
        id: 0,
        type: phoneType1 && phoneType1?.length > 0 ? phoneType1[0] : "",
        type_id: phoneType1 && phoneType1?.length > 0 ? phoneType1[0]?.phone_type_id : "",
        auto_notify: protected_address?.is_auto_notify ? false : true,
      });
    }
    if (protected_address?.cellphone) {
      cloneData?.push({
        phoneNumber: protected_address?.cellphone,
        id: 1,
        type: phoneType2 && phoneType2?.length > 0 ? phoneType2[0] : "",
        type_id: phoneType2 && phoneType2?.length > 0 ? phoneType2[0]?.phone_type_id : "",
        auto_notify: protected_address?.is_cell_auto_notify ? false : true,
      });
    }
    setPhoneList(cloneData);
  }, [medicalUserDetail, phoneTypeList, allRegionList?.length]);

  useEffect(() => {
    getRegionList({
      dispatch,
      userType: "accountowner",
      is_visible: false,
      is_for_mau: true
    });
    getPhoneTypeList({
      dispatch,
      history,
      userType: "accountowner",
    });
  }, [dispatch, history]);

  useEffect(() => {
    if (allRegionList?.length > 0 && medicalUserDetail && phoneTypeList?.length > 0) {
      // Check if medical user data has actually changed (by comparing phone numbers)
      const hasDataChanged =
        previousMedicalDataRef.current?.protected_address?.mobile !== medicalUserDetail?.protected_address?.mobile ||
        previousMedicalDataRef.current?.protected_address?.cellphone !== medicalUserDetail?.protected_address?.cellphone ||
        previousMedicalDataRef.current === null;

      if (hasDataChanged) {
        setInitialData();
        previousMedicalDataRef.current = medicalUserDetail;
      }
    }
  }, [allRegionList, medicalUserDetail, phoneTypeList, setInitialData]);


  const handleFieldChange = (type, value, isNumeric = false) => {
    let numericText = value;
    if (isNumeric) {
      numericText = value.replace(/[^0-9]/g, "");
    }
    helpers.handleValueChange(type, numericText, {
      subscriberState,
      setSubscriberState,
      validationTriggered,
      errorState,
      setErrorState,
      snackbarState,
      onApiResponse,
      dismissSnackbar
    });
  };

  const onAddContact = () => {
    setEditingContact(null);
    setSelectedPhoneType(dispatch, null);
    setShowAddContactModal(true);
    setNewContact({
      phoneNumber: "",
      type: "",
      type_id: "",
      auto_notify: false,
    });
  };

  const onEditContact = (contact) => {
    // Set the phone type in Redux BEFORE opening the modal (like AccountHolder)
    setSelectedPhoneType(dispatch, contact?.type);
    setEditingContact(contact);
    setNewContact({
      phoneNumber: contact?.phoneNumber,
      type: contact?.type,
      type_id: contact?.type_id,
      auto_notify: contact?.auto_notify,
    });
    setShowAddContactModal(true);
  };

  const onSaveContact = (contactData) => {
    let updatedList;

    // Extract type_id from contactData.type
    const typeId = contactData?.type?.phone_type_id || contactData?.type_id;

    if (editingContact) {
      // Update existing contact
      updatedList = phoneList?.map((contact) =>
        contact.id === editingContact?.id
          ? {
            ...contact,
            phoneNumber: contactData?.phoneNumber,
            type: contactData?.type,
            type_id: typeId,
            auto_notify: contactData?.isAcceptSignature,
          }
          : contact
      );
    } else {
      // Add new contact
      const newContactWithId = {
        id: phoneList?.length,
        phoneNumber: contactData?.phoneNumber,
        type: contactData?.type,
        type_id: typeId,
        auto_notify: contactData?.isAcceptSignature,
      };
      updatedList = [...phoneList, newContactWithId];
    }

    handlePhoneListChange(updatedList);
    setShowAddContactModal(false);
    setEditingContact(null);
    setSelectedPhoneType(dispatch, null);
    setNewContact({
      phoneNumber: "",
      type: "",
      type_id: "",
      auto_notify: false,
    });
  };

  const onDeleteContact = (contactToDelete) => {
    setDeleteContactModal(contactToDelete);
  };

  const confirmDeleteContact = () => {
    if (deleteContactModal) {
      const updatedList = phoneList.filter(
        (contact) => contact?.id !== deleteContactModal?.id
      );
      handlePhoneListChange(updatedList);

      // Close edit modal if the deleted contact was being edited
      if (editingContact && editingContact.id === deleteContactModal.id) {
        setShowAddContactModal(false);
        setEditingContact(null);
        setSelectedPhoneType(dispatch, null);
      }

      setDeleteContactModal(false);
    }
  };

  // Handle phone list changes and clear phoneList error
  const handlePhoneListChange = (newPhoneList) => {
    helpers.handlePhoneListChange(newPhoneList, {
      setPhoneList,
      validationTriggered,
      errorState,
      setErrorState,
      snackbarState,
      onApiResponse,
      dismissSnackbar,
      VALIDATION_FIELDS
    });
  };

  const handleAddContactModalClose = () => {
    setShowAddContactModal(false);
    setEditingContact(null);
    setSelectedPhoneType(dispatch, null);
  };

  const handleDragStart = (e, index) => {
    e?.dataTransfer?.setData("text/plain", index);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e, dropIndex) => {
    e?.preventDefault();
    const dragIndex = parseInt(e.dataTransfer.getData("text/plain"));

    if (dragIndex !== dropIndex) {
      const newPhoneList = [...phoneList];
      const draggedItem = newPhoneList[dragIndex];
      newPhoneList?.splice(dragIndex, 1);
      newPhoneList?.splice(dropIndex, 0, draggedItem);
      setPhoneList(newPhoneList);
    }
  };

  const handleProtectedAddressHelp = () => {
    showModal(titles?.modal_texts?.protected_address_help_tip);
  };

  const handleDateOfBirthHelp = () => {
    showModal(titles?.modal_texts?.dob_help_tip);
  };

  const handlePhoneContactHelp = () => {
    showModal(titles?.modal_texts?.phone_no_help_tip);
  };

  const showModal = (description) => {
    setInfoModalPopup({
      description,
      isVisible: true,
    });
  };

  const handleOnSubmit = async () => {
    if (!subscriberState?.isAcceptSignature) return;

    await helpers.handleSubmit({
      subscriberState,
      phoneList,
      phoneTypeList,
      medicalUserDetail,
      allRegionList,
      userData,
      setValidationTriggered,
      setErrorState,
      onApiResponse,
      dispatch,
      history,
      handleApiResponse,
      setChangesSaveModal,
      VALIDATION_FIELDS
    });
  };

  const handleDropdownSelect = (value, id) => {
    handleFieldChange("state", { id: id, value: value });
  };

  // Check if there are any validation errors
  const hasValidationErrors = Object.values(errorState.fieldErrors).some(error => error);

  // Don't auto-close snackbar if there are validation errors
  const snackbarAutoHideDuration = hasValidationErrors && validationTriggered
    ? 0
    : snackbarState?.autoHideDuration;

  return (
    <MainLayout
      text={`${titles?.edit} ${medicalUserDetail?.firstname}'s ${titles?.details}`}
      isBackHide={false}
      isNavbar={true}
      white={true}
      isLoading={loading}

    >
      {/* Modals */}
      <ConfirmationModal
        show={!!deleteContactModal}
        setShow={setDeleteContactModal}
        svgIcon={<IconTrash color={"#DB4E3B"} />}
        title={titles?.delete_contact}
        description={titles?.are_you_sure_to_remove_number}
        btnText={titles?.remove}
        onClick={confirmDeleteContact}
      />

      <ChangesSavedModal
        show={changesSavedModal}
        setShow={setChangesSaveModal}
      />

      <InformationModal
        show={infoModalPopup?.isVisible}
        setShow={setInfoModalPopup}
        infoText={infoModalPopup?.description}
      />

      <div className={`esd_container ${snackbarState?.show ? 'esd_snackbar_visible' : ''}`}>
        <SubscriberInformationCard medicalUserDetail={medicalUserDetail} />
        {snackbarState?.show && (
          <>
            <div className="esd_snackbar_placeholder" />
            <div
              className="esd_snackbar_animated"
              style={{ top: `${snackbarTop}px` }}
            >
              <MessageSnackBar
                message={snackbarState?.message}
                isVisible={true}
                type={snackbarState?.type}
                autoHideDuration={snackbarAutoHideDuration}
                constrainWidth={false}
                onDismiss={dismissSnackbar}
              />
            </div>
          </>
        )}

        {/* Protected Address Section */}
        <div className="esd_section esd_protected_container">
          <div className="esd_section_header">
            <h3 className="display_h3">{titles?.protected_address}</h3>
            <IconQuestion
              onClick={handleProtectedAddressHelp}
              {...ICON_PROPS.questionIcon}
            />
          </div>

          <div className="esd_address_fields">
            <CustomInput
              placeholder={titles?.street}
              type="text"
              value={subscriberState?.street}
              onChange={(e) =>
                handleFieldChange("street", e?.target?.value, false)
              }
              required
              error={validationTriggered && errorState.fieldErrors?.street && errorState.show}
              errorMessage={validationTriggered && errorState.fieldErrors?.street && errorState.show ? errorState.fieldMessages?.street : ""}
            />
            <CustomInput
              placeholder={titles?.apt}
              type="text"
              value={subscriberState?.apt || ""}
              onChange={(e) =>
                handleFieldChange("apt", e?.target?.value, false)
              }
            />
            <CustomInput
              placeholder={titles?.city}
              type="text"
              value={subscriberState?.city}
              onChange={(e) =>
                handleFieldChange("city", e?.target?.value, false)
              }
              required
              error={validationTriggered && errorState.fieldErrors?.city && errorState.show}
              errorMessage={validationTriggered && errorState.fieldErrors?.city && errorState.show ? errorState.fieldMessages?.city : ""}
            />
            <CustomSelectBox
              required={true}
              options={getAllRegionListNames(allRegionList)}
              title={titles?.state}
              selectedValue={subscriberState?.state?.value}
              onChange={(item) => handleDropdownSelect(item?.value, item?.id)}
              error={validationTriggered && errorState.fieldErrors?.state && errorState.show}
              errorMessage={validationTriggered && errorState.fieldErrors?.state && errorState.show ? errorState.fieldMessages?.state : ""}
            />
            <CustomInput
              placeholder={titles?.zip_code}
              type="text"
              value={subscriberState?.zip_code}
              onChange={(e) =>
                handleFieldChange("zip_code", e?.target?.value, false)
              }
              required
              error={validationTriggered && errorState.fieldErrors?.zip_code && errorState.show}
              errorMessage={validationTriggered && errorState.fieldErrors?.zip_code && errorState.show ? errorState.fieldMessages?.zip_code : ""}
            />
          </div>
        </div>

        {/* Date of Birth Section */}
        <div className="esd_section">
          <div className="esd_section_header">
            <h3 className="display_h3">{titles?.date_of_birth}</h3>
            <IconQuestion
              onClick={handleDateOfBirthHelp}
              {...ICON_PROPS.questionIcon}
            />
          </div>
          <div className="esd_date_picker_container">
            <CustomDatePicker
              placeholder={titles?.date_of_birth}
              selected={subscriberState?.dateOfBirth}
              onChange={(date) => handleFieldChange("dateOfBirth", date, false)}
              error={validationTriggered && errorState.fieldErrors?.dateOfBirth && errorState.show}
              errorMessage={validationTriggered && errorState.fieldErrors?.dateOfBirth && errorState.show ? errorState.fieldMessages?.dateOfBirth : ""}
            />
          </div>
        </div>

        {/* Phone Contact Section */}
        <div className="esd_section">
          <div className="esd_section_header">
            <h3 className="display_h3">{titles?.phone_contact}</h3>
            <IconQuestion
              onClick={handlePhoneContactHelp}
              {...ICON_PROPS.questionIcon}
            />
          </div>
          <p className="body_h1 left">
            {titles?.numbers_used_fo_account_management}
          </p>
          <p className="body_h1 left mb10">{titles?.hold_drag_item_desc}</p>

          {phoneList?.map((contact, index) => (
            <div
              key={contact.id}
              className="esd_phone_contact_item"
              draggable
              data-drag-index={index}
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, index)}
            >
              <DragHandleIcon className="esd_drag_handle" />
              <div className="esd_phone_info">
                <p className="esd_phone_number">
                  {formatPhoneNumber(contact?.phoneNumber)}
                </p>
                <IconEdit
                  className="esd_edit_icon"
                  {...ICON_PROPS.actionIcon}
                  onClick={() => onEditContact(contact)}
                />
                <IconTrash
                  className="esd_delete_icon"
                  {...ICON_PROPS.actionIcon}
                  onClick={() => onDeleteContact(contact)}
                />
              </div>
            </div>
          ))}

          {phoneList?.length < 2 && !showAddContactModal && (
            <button className="esd_add_contact_link" onClick={onAddContact}>
              {titles?.add_contact}
            </button>
          )}

          {/* Inline Add/Edit Contact Form */}
          {showAddContactModal && (
            <AddAccountContactModal
              onClose={handleAddContactModalClose}
              onSave={onSaveContact}
              phoneInfo={editingContact || newContact}
              onDeleteContact={onDeleteContact}
            />
          )}
        </div>

        {/* Digital Signature Section */}
        <div className="esd_section">
          <h3 className="display_h3 left">{titles?.digital_signature}</h3>
          <div className="mb18">
            <p
              className="body_h1 eds_gray_text"
              style={{ whiteSpace: "pre-line" }}
            >
              {titles?.degital_signature_terms_condition}
            </p>
          </div>
          <div className="consent_note_container">
            <CustomCheckbox
              className="acm_checkbox_field"
              checked={subscriberState?.isAcceptSignature}
              onChange={() => {
                handleFieldChange(
                  "isAcceptSignature",
                  !subscriberState?.isAcceptSignature,
                  false
                );
              }}
            />
            <div>
              <p className="esd_checkbox_text new_line_text">
                {`**${accountHolderData?.firstname +
                  " " +
                  accountHolderData?.lastname
                  }\nc/o ${medicalUserDetail?.firstname +
                  " " +
                  medicalUserDetail?.lastname
                  }**`}
              </p>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <CustomButton
          text={titles?.submit}
          onClick={handleOnSubmit}
          width={"100%"}
          disabled={!subscriberState?.isAcceptSignature || showAddContactModal}
        />
      </div>
    </MainLayout>
  );
};

export default EditSubscriberDetails;
